import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, Alert } from "react-bootstrap";
import { useCompanies } from "../../../../context/CompanyContext";
import { getSimCards } from "../../../../api/simCardApi";
import PropTypes from "prop-types";
import { PRODUCT_STATUS } from "../../../../constants/productConstants";
import classes from "./AddProductModal.module.scss";
import { validateProduct } from "../../../../validation/productValidation";
import { getUnassignedBases } from "../../../../api/baseApi";

const AddProductModal = ({
  t,
  show,
  onClose,
  onSave,
  productTypes = [],
  tableType = "products",
}) => {
  const { companies } = useCompanies();
  const [validated, setValidated] = useState(false);
  const [error, setError] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [simCards, setSimCards] = useState([]);
  const [loadingSimCards, setLoadingSimCards] = useState(false);
  const [selectedSimCard, setSelectedSimCard] = useState("");
  const [unassignedBases, setUnassignedBases] = useState([]);
  const [selectedBase, setSelectedBase] = useState("");
  const [formData, setFormData] = useState({
    baseid: "",
    probeid: "",
    regnumber: "",
    probelength: "",
    probesensnum: "",
    imeei: "",
    sim: "",
    comments: "",
    status: "T", // Default to Test
    companies_id: "",
    sim_id: "",
    probe_type: "",
    product_types_id: "",
  });

  // Fetch SIM cards when modal opens
  useEffect(() => {
    if (show) {
      fetchSimCards();
    }
  }, [show]);

  // Function to fetch SIM cards
  const fetchSimCards = async () => {
    try {
      setLoadingSimCards(true);
      const data = await getSimCards();
      // Sort SIM cards by cardname
      // Natural sort that correctly handles numbers within strings
      data.sort((a, b) => {
        // Extract any number sequences from the card names
        const aMatches = a.cardname.match(/(\d+)/g);
        const bMatches = b.cardname.match(/(\d+)/g);

        if (aMatches && bMatches) {
          // If both have numbers, compare the first number in each
          const aNum = parseInt(aMatches[0]);
          const bNum = parseInt(bMatches[0]);
          if (aNum !== bNum) {
            return aNum - bNum; // Numeric comparison
          }
        }

        // Fall back to string comparison if numbers are equal or not present
        return a.cardname.localeCompare(b.cardname);
      });
      setSimCards(data);
    } catch (error) {
      console.error("Error fetching SIM cards:", error);
      setError(t("error_fetching_simcards"));
    } finally {
      setLoadingSimCards(false);
    }
  };

  // Function to fetch unassigned bases
  const fetchUnassignedBases = async () => {
    try {
      const result = await getUnassignedBases();
      // Sort unassigned bases by baseid
      result.sort((a, b) => {
        // Extract any number sequences from the baseids
        const aMatches = a.baseid.match(/(\d+)/g);
        const bMatches = b.baseid.match(/(\d+)/g);
        if (aMatches && bMatches) {
          // If both have numbers, compare the first number in each
          const aNum = parseInt(aMatches[0]);
          const bNum = parseInt(bMatches[0]);
          if (aNum !== bNum) {
            return aNum - bNum; // Numeric comparison
          }
        }
        // Fall back to string comparison if numbers are equal or not present
        return a.baseid.localeCompare(b.baseid);
      });
      // Set the unassigned bases in state
      setUnassignedBases(result);
    } catch (error) {
      console.error("Error fetching unassigned bases:", error);
      setError(t("error_fetching_unassigned_bases"));
    }
  };

  // Fetch unassigned bases when modal opens
  useEffect(() => {
    if (show) {
      fetchUnassignedBases();
    }
  }, [show]);

  // Handle SIM card selection
  const handleSimCardChange = (e) => {
    const simCardId = e.target.value;
    setSelectedSimCard(simCardId);

    if (!simCardId) {
      // If no SIM card is selected, clear the fields
      setFormData((prev) => ({
        ...prev,
        sim: "",
        sim_id: "",
      }));
      return;
    }

    // Find the selected SIM card and update form data
    const selectedCard = simCards.find(
      (card) => card._id === parseInt(simCardId)
    );
    if (selectedCard) {
      setFormData((prev) => ({
        ...prev,
        sim: selectedCard.sim,
        sim_id: selectedCard._id.toString(),
      }));
    }
  };

  // Handle base selection
  const handleBaseChange = (e) => {
    const baseid = e.target.value;
    setSelectedBase(baseid);

    if (!baseid) {
      // If no base is selected, clear the baseid field
      setFormData((prev) => ({
        ...prev,
        baseid: "",
        sim_id: "",
        imeei: "",
        sim: "",
      }));
      return;
    }
    // Find the selected base and update form data
    const selectedBase = unassignedBases.find((base) => base.baseid === baseid);
    if (selectedBase) {
      setFormData((prev) => ({
        ...prev,
        baseid: selectedBase.baseid || "",
        sim_id: selectedBase.sim_id || "",
        imeei: selectedBase.imeei || "",
        sim: selectedBase.sim || "",
      }));
    }
  };

  // Product type mappings for auto-fill
  const PRODUCT_TYPE_MAPPINGS = {
    3: { probe_type: "Flexible", probelength: 13, probesensnum: 6 },
    4: { probe_type: "Flexible", probelength: 10, probesensnum: 6 },
    5: { probe_type: "Fix", probelength: 4, probesensnum: 4 },
    6: { probe_type: "Fix", probelength: 4, probesensnum: 3 },
  };

  // Handle product type change
  const handleProductTypeChange = (e) => {
    const { value } = e.target;
    const numericValue = parseInt(value, 10); // Convert string to number
    if (isNaN(numericValue)) {
      console.error("Invalid product type ID selected:", value);
      setFormData((prev) => ({
        ...prev,
        product_types_id: "", // Reset if invalid
      }));
      return;
    }
    // Update the product type ID in form data
    setFormData((prev) => ({
      ...prev,
      product_types_id: value, // Keep as string for form state
    }));

    // if the selected product type is a base automatically fill with "Base"
    if (numericValue === 1 || numericValue === 2) {
      setFormData((prev) => ({
        ...prev,
        probe_type: "Base",
        probelength: "",
        probesensnum: "",
      }));
      return; // No need to auto-fill probe details for base products
    }
    // If we have mapping data for this product type, auto-fill the fields
    if (value && PRODUCT_TYPE_MAPPINGS[numericValue]) {
      const mapping = PRODUCT_TYPE_MAPPINGS[numericValue];

      setFormData((prev) => ({
        ...prev,
        probe_type: mapping.probe_type,
        probelength: mapping.probelength.toString(),
        probesensnum: mapping.probesensnum.toString(),
      }));
    }
  };

  // Helper function to determine product type
  const isBaseProduct = () => {
    const productTypeId = parseInt(formData.product_types_id, 10);
    return productTypeId === 1 || productTypeId === 2;
  };

  // Helper function to determine if the product is a probe
  const isProbeProduct = () => {
    const productTypeId = parseInt(formData.product_types_id, 10);
    return [3, 4, 5, 6].includes(productTypeId);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Clone formData to avoid modifying state directly
    const formDataToSubmit = { ...formData };

    // Convert numeric fields to numbers
    formDataToSubmit.probeid = parseInt(formDataToSubmit.probeid, 10);
    formDataToSubmit.probelength = parseInt(formDataToSubmit.probelength, 10);
    formDataToSubmit.probesensnum = parseInt(formDataToSubmit.probesensnum, 10);
    formDataToSubmit.companies_id = parseInt(formDataToSubmit.companies_id, 10);
    formDataToSubmit.product_types_id = parseInt(
      formDataToSubmit.product_types_id,
      10
    );
    formDataToSubmit.sim_id = parseInt(formDataToSubmit.sim_id, 10);

    // Convert empty strings to null for specific fields
    if (isProbeProduct()) {
      // For probe products, baseid should be null, not empty string
      formDataToSubmit.baseid = null;
      formDataToSubmit.imeei = null;
      formDataToSubmit.sim = null;
      formDataToSubmit.sim_id = null;
      if (formDataToSubmit.comments.length === 0) {
        formDataToSubmit.comments = null; // Set comments to null if empty
      }
    }

    if (isBaseProduct()) {
      // For base products, probeid should be null, not empty string
      formDataToSubmit.probeid = null;
      formDataToSubmit.probelength = null;
      formDataToSubmit.probesensnum = null;

      if (formDataToSubmit.comments.length === 0) {
        formDataToSubmit.comments = null; // Set comments to null if empty
      }
    }

    // Additional manual validation
    const errors = validateProduct(formDataToSubmit);
    // Store validation errors in state to display in the form
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) {
      // Still show a general error message at the top
      setError(t("please_fix_validation_errors"));
      return;
    }

    try {
      const result = await onSave(formDataToSubmit); // Add await here

      // Check if submission was successful
      if (!result.success) {
        // If we have a field-specific error, highlight that field
        if (result.errorField) {
          setValidationErrors((prev) => ({
            ...prev,
            [result.errorField]: result.errorCode,
          }));
        }

        // Show the error message at the top of the form
        setError(result.error);
        return;
      }

      // If submission was successful, reset the form
      // Reset form after successful save
      setFormData({
        baseid: "",
        probeid: "",
        regnumber: "",
        probelength: "",
        probesensnum: "",
        imeei: "",
        sim: "",
        comments: "",
        status: "T",
        companies_id: "",
        sim_id: "",
        probe_type: "",
        product_types_id: "",
      });
      // Clear validation errors when form is valid
      setValidationErrors({});
      setValidated(false);
      setError("");
      onClose();
    } catch (err) {
      setError(t("error_saving_product"));
    }
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      size="lg"
      centered
      className={classes.addProductModal}
    >
      <Modal.Header closeButton>
        <Modal.Title>{t("add_product")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <Form noValidate validated={validated} onSubmit={handleSubmit}>
          <Row>
            <Col xs={12} md={6} className={classes.formGroup}>
              <Form.Label>{t("product_type")}</Form.Label>
              <Form.Control
                as="select"
                name="product_types_id"
                value={formData.product_types_id}
                onChange={handleProductTypeChange}
                required
                isInvalid={!!validationErrors.product_types_id}
                className={`${
                  tableType ? `modal-${tableType}-field-product_types_id` : ""
                }`}
              >
                <option value="">{t("choose")}</option>
                {productTypes.map((type) => (
                  <option key={type._id} value={type._id}>
                    {type.product_longname}
                  </option>
                ))}
              </Form.Control>
            </Col>

            {/* Registration Number */}
            <Col xs={12} md={6} className={classes.formGroup}>
              <Form.Group controlId="regnumber">
                <Form.Label>{t("regnumber")}</Form.Label>
                <Form.Control
                  type="text"
                  name="regnumber"
                  value={formData.regnumber}
                  onChange={handleChange}
                  required
                  isInvalid={!!validationErrors.regnumber}
                  className={`${
                    tableType ? `modal-${tableType}-field-regnumber` : ""
                  }`}
                />
                <Form.Control.Feedback type="invalid">
                  {validationErrors.regnumber
                    ? t(validationErrors.regnumber)
                    : t("required_field")}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            {/* Basic Identifiers */}
            {isBaseProduct() && (
              <Col xs={12} md={6} className={classes.formGroup}>
                <Form.Group controlId="baseid">
                  <Form.Label>{t("baseid")}</Form.Label>
                  <Form.Control
                    as="select"
                    name="baseid"
                    value={formData.baseid}
                    onChange={handleBaseChange}
                    required
                    isInvalid={!!validationErrors.baseid}
                    className={`${
                      tableType ? `modal-${tableType}-field-baseid` : ""
                    }`}
                  >
                    <option value="">{t("choose")}</option>
                    {unassignedBases.map((base) => (
                      <option key={base._id} value={base.baseid}>
                        {base.baseid}
                      </option>
                    ))}
                  </Form.Control>
                  <Form.Control.Feedback type="invalid">
                    {validationErrors.baseid
                      ? t(validationErrors.baseid)
                      : t("required_field")}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            )}

            {isProbeProduct() && (
              <Col xs={12} md={6} className={classes.formGroup}>
                <Form.Group controlId="probeid">
                  <Form.Label>{t("probeid")}</Form.Label>
                  <Form.Control
                    type="text"
                    name="probeid"
                    value={formData.probeid}
                    onChange={handleChange}
                    required
                    isInvalid={!!validationErrors.probeid}
                    className={`${
                      tableType ? `modal-${tableType}-field-probeid` : ""
                    }`}
                  />
                  <Form.Control.Feedback type="invalid">
                    {validationErrors.probeid
                      ? t(validationErrors.probeid)
                      : t("required_field")}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            )}
            {/* Probe Details */}
            {isProbeProduct() && (
              <Col xs={12} md={6} className={classes.formGroup}>
                <Form.Group controlId="probelength">
                  <Form.Label>{t("probelength")}</Form.Label>
                  <Form.Control
                    type="number"
                    name="probelength"
                    value={formData.probelength}
                    readOnly
                    className={`${
                      tableType ? `modal-${tableType}-field-probelength` : ""
                    } ${classes.autofilled}`}
                  />
                </Form.Group>
              </Col>
            )}
            {isProbeProduct() && (
              <Col xs={12} md={6} className={classes.formGroup}>
                <Form.Group controlId="probesensnum">
                  <Form.Label>{t("probesensnum")}</Form.Label>
                  <Form.Control
                    type="number"
                    name="probesensnum"
                    value={formData.probesensnum}
                    readOnly
                    className={`${
                      tableType ? `modal-${tableType}-field-probesensnum` : ""
                    } ${classes.autofilled}`}
                  />
                </Form.Group>
              </Col>
            )}
            {/* Device Identifiers */}
            {/* {!isProbeProduct() && (
              <Col xs={12} md={6} className={classes.formGroup}>
                <Form.Group controlId="imeei">
                  <Form.Label>{t("imeei")}</Form.Label>
                  <Form.Control
                    type="text"
                    name="imeei"
                    value={formData.imeei}
                    onChange={handleChange}
                    required
                    readOnly
                    isInvalid={!!validationErrors.imeei}
                    className={`${
                      tableType ? `modal-${tableType}-field-imeei` : ""
                    }`}
                  />
                  <Form.Control.Feedback type="invalid">
                    {validationErrors.imeei
                      ? t(validationErrors.imeei)
                      : t("required_field")}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            )}
 */}
           {/*  {!isProbeProduct() && (
              <Col xs={12} md={6} className={classes.formGroup}>
                <Form.Group controlId="selectedSimCard">
                  <Form.Label>{t("sim")}</Form.Label>
                  <Form.Control
                    type="text"
                    name="sim"
                    value={formData.sim}
                    onChange={handleChange}
                    required
                    readOnly
                    isInvalid={
                      !!validationErrors.sim || !!validationErrors.sim_id
                    }
                    disabled={loadingSimCards}
                    className={`${
                      tableType ? `modal-${tableType}-field-sim_card` : ""
                    }`}
                    />
                  <Form.Control.Feedback type="invalid">
                    {validationErrors.sim
                      ? t(validationErrors.sim)
                      : validationErrors.sim_id
                      ? t(validationErrors.sim_id)
                      : t("required_field")}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            )} */}

            {/* Hidden fields for SIM values (still needed for form submission) */}
            {!isProbeProduct() && (
              <>
                <input type="hidden" name="imeei" value={formData.imeei || ""} />
                <input type="hidden" name="sim" value={formData.sim || ""} />
                <input type="hidden" name="sim_id" value={formData.sim_id || ""} />
              </>
            )}
            {/* Status and Type */}
            <Col xs={12} md={6} className={classes.formGroup}>
              <Form.Group controlId="status">
                <Form.Label>{t("status")}</Form.Label>
                <Form.Control
                  as="select"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  required
                  isInvalid={!!validationErrors.status}
                  className={`${
                    tableType ? `modal-${tableType}-field-status` : ""
                  }`}
                >
                  {Object.entries(PRODUCT_STATUS).map(([key, value]) => (
                    <option key={key} value={key}>
                      {t(value)}
                    </option>
                  ))}
                </Form.Control>
                <Form.Control.Feedback type="invalid">
                  {validationErrors.status
                    ? t(validationErrors.status)
                    : t("required_field")}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col xs={12} md={6} className={classes.formGroup}>
              <Form.Group controlId="probe_type">
                <Form.Label>{t("probe_type")}</Form.Label>
                <Form.Control
                  type="text"
                  name="probe_type"
                  value={formData.probe_type}
                  readOnly
                  required
                  className={`${
                    tableType ? `modal-${tableType}-field-probe_type` : ""
                  } ${classes.autofilled}`}
                />
              </Form.Group>
            </Col>

            {/* Dropdowns */}
            <Col xs={12} md={6} className={classes.formGroup}>
              <Form.Group controlId="companies_id">
                <Form.Label>{t("company")}</Form.Label>
                <Form.Control
                  as="select"
                  name="companies_id"
                  value={formData.companies_id}
                  onChange={handleChange}
                  required
                  isInvalid={!!validationErrors.companies_id}
                  className={`${
                    tableType ? `modal-${tableType}-field-companies_id` : ""
                  }`}
                >
                  <option value="">{t("choose")}</option>
                  {companies.map((company) => (
                    <option key={company.companyid} value={company.companyid}>
                      {company.companyname}
                    </option>
                  ))}
                </Form.Control>
                <Form.Control.Feedback type="invalid">
                  {validationErrors.companies_id
                    ? t(validationErrors.companies_id)
                    : t("required_field")}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            {/* Comments - Full Width */}
            <Col xs={12} className={classes.formGroup}>
              <Form.Group controlId="comments">
                <Form.Label>{t("comments")}</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="comments"
                  value={formData.comments}
                  onChange={handleChange}
                  isInvalid={!!validationErrors.comments}
                  className={`${
                    tableType ? `modal-${tableType}-field-comments` : ""
                  }`}
                />
                <Form.Control.Feedback type="invalid">
                  {validationErrors.comments
                    ? t(validationErrors.comments)
                    : t("required_field")}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          {t("cancel")}
        </Button>
        <Button variant="primary" onClick={handleSubmit}>
          {t("save")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

AddProductModal.propTypes = {
  t: PropTypes.func.isRequired,
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  productTypes: PropTypes.array,
  tableType: PropTypes.string,
};

export default AddProductModal;
